import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { generateBookingUrl, getTomorrowDate } from '../../utils/bookingUtils';
import { Menu, X, Phone, Zap } from 'lucide-react';

function Header() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const navigationItems = [
    { path: '/price-list', label: 'Scrap Rates' },
    { path: '/services', label: 'Services' },
    { path: '/e-waste-scrap', label: 'E-Waste Scrap' },
    { path: '/contact-us', label: 'Contact Us' },
  ];

  return (
    <motion.header
      className="
        fixed top-0 left-0 right-0 z-50
        w-full
        bg-white
        border-b border-[#DDE8DF]
        shadow-[0_3px_15px_rgba(16,32,24,0.05)]
        overflow-hidden
      "
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.45 }}
    >

      {/* =====================================================
          RUNNING ANNOUNCEMENT BAR
      ====================================================== */}
      <div
        className="
          relative
          w-full
          h-[30px]
          overflow-hidden
          bg-[#2F9621]
          border-b border-[#DDE8DF]
          flex items-center
        "
      >
        <div className="announcement-track">
          <span>Chandigarh • Mohali • Panchkula — Your Trusted Scrap Pickup Partner</span>

          <span>Chandigarh • Mohali • Panchkula — Your Trusted Scrap Pickup Partner</span>

          <span>Chandigarh • Mohali • Panchkula — Your Trusted Scrap Pickup Partner </span>

          <span>Chandigarh • Mohali • Panchkula — Your Trusted Scrap Pickup Partner</span>
        </div>
      </div>

      {/* =====================================================
          MAIN NAVBAR
      ====================================================== */}
      <div className="w-full px-5 lg:px-8 xl:px-10 2xl:px-12">

        <div
          className="
            h-[64px]
            flex
            items-center
            w-full
          "
        >

          {/* =================================================
              LOGO
          ================================================== */}
          <motion.div
            className="flex-shrink-0"
            whileHover={{ scale: 1.015 }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 18,
            }}
          >
            <Link
              to="/"
              className="
                flex
                items-center
                gap-2.5
                whitespace-nowrap
              "
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <img
                src="/logo.png"
                alt="ScrapSector"
                className="
                  w-auto
                  h-[35px]
                  object-contain
                  flex-shrink-0
                "
              />

              <span
                className="
                  text-[27px]
                  font-heading
                  font-bold
                  leading-none
                  tracking-[-0.7px]
                  text-[#102018]
                "
              >
                Scrap<span className="text-[#2F6F45]">Sector</span>
              </span>
            </Link>
          </motion.div>

          {/* =================================================
              DESKTOP NAVIGATION
          ================================================== */}
          <nav
            className="
              hidden
              lg:flex
              items-center
              flex-1
              ml-12
            "
          >
            <div className="flex items-center gap-1">

              {navigationItems.map((item) => (
                <motion.div
                  key={item.path}
                  whileHover={{ y: -1 }}
                >
                  <Link
                    to={item.path}
                    className={`
                      relative
                      flex
                      items-center
                      justify-center
                      h-[46px]
                      px-4
                      rounded-[14px]
                      whitespace-nowrap
                      text-[15px]
                      font-medium
                      transition-all
                      duration-200
                      ${
                        isActive(item.path)
                          ? 'text-[#2F6F45] bg-[#EAF5EC]'
                          : 'text-[#718078] hover:text-[#2F6F45] hover:bg-[#F6F8F4]'
                      }
                    `}
                  >
                    {item.label}

                    {isActive(item.path) && (
                      <motion.span
                        layoutId="activeTab"
                        className="
                          absolute
                          bottom-[5px]
                          left-[20px]
                          right-[20px]
                          h-[2px]
                          rounded-full
                          bg-[#70BD7D]
                        "
                        transition={{
                          type: 'spring',
                          stiffness: 500,
                          damping: 30,
                        }}
                      />
                    )}
                  </Link>
                </motion.div>
              ))}

            </div>
          </nav>

          {/* =================================================
              RIGHT SIDE ACTIONS
          ================================================== */}
          <div
            className="
              hidden
              lg:flex
              items-center
              gap-2
              ml-auto
              flex-shrink-0
            "
          >

            {/* My Bookings */}
            <Link
              to="/my-bookings"
              className="
                flex
                items-center
                justify-center
                h-[46px]
                px-3
                rounded-[14px]
                whitespace-nowrap
                text-[15px]
                font-medium
                text-[#718078]
                hover:text-[#2F6F45]
                hover:bg-[#F6F8F4]
                transition-all
                duration-200
              "
            >
              My Bookings
            </Link>

            {/* Book Now */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link
                to="/book-appointment"
                title="Book a pickup appointment"
                className="
                  flex
                  items-center
                  justify-center
                  gap-2
                  h-[44px]
                  px-5
                  rounded-[14px]
                  whitespace-nowrap
                  bg-[#102018]
                  border
                  border-[#102018]
                  text-white
                  text-[15px]
                  font-semibold
                  shadow-[0_5px_15px_rgba(16,32,24,0.12)]
                  transition-all
                  duration-300
                  hover:bg-[#2F6F45]
                  hover:border-[#2F6F45]
                "
              >
                <Phone className="w-[17px] h-[17px]" />
                <span>Book Now</span>
              </Link>
            </motion.div>

            {/* Quick Book */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link
                to={generateBookingUrl(getTomorrowDate(), 'morning')}
                title="Quick book for tomorrow morning"
                className="
                  flex
                  items-center
                  justify-center
                  gap-2
                  h-[44px]
                  px-5
                  rounded-[14px]
                  whitespace-nowrap
                  bg-[#2F6F45]
                  border
                  border-[#2F6F45]
                  text-white
                  text-[15px]
                  font-semibold
                  shadow-[0_5px_15px_rgba(47,111,69,0.15)]
                  transition-all
                  duration-300
                  hover:bg-[#70BD7D]
                  hover:border-[#70BD7D]
                  hover:text-[#102018]
                "
              >
                <Zap className="w-[17px] h-[17px]" />
                <span>Quick Book</span>
              </Link>
            </motion.div>

          </div>

          {/* =================================================
              MOBILE MENU BUTTON
          ================================================== */}
          <motion.button
            type="button"
            className="
              lg:hidden
              ml-auto
              flex
              items-center
              justify-center
              w-10
              h-10
              rounded-xl
              text-[#102018]
              hover:text-[#2F6F45]
              hover:bg-[#EAF5EC]
              transition-all
              duration-200
            "
            onClick={toggleMobileMenu}
            whileTap={{ scale: 0.94 }}
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            <AnimatePresence mode="wait">

              {isMobileMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className="w-6 h-6" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu className="w-6 h-6" />
                </motion.div>
              )}

            </AnimatePresence>
          </motion.button>

        </div>

        {/* =====================================================
            MOBILE MENU
        ====================================================== */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="
                lg:hidden
                border-t
                border-[#DDE8DF]
                bg-white
              "
              initial={{
                height: 0,
                opacity: 0,
              }}
              animate={{
                height: 'auto',
                opacity: 1,
              }}
              exit={{
                height: 0,
                opacity: 0,
              }}
              transition={{
                duration: 0.25,
              }}
            >

              <div className="py-5 space-y-1.5">

                {/* Mobile Navigation */}
                {navigationItems.map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{
                      x: -15,
                      opacity: 0,
                    }}
                    animate={{
                      x: 0,
                      opacity: 1,
                    }}
                    transition={{
                      delay: index * 0.05,
                    }}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`
                        flex
                        items-center
                        w-full
                        h-[46px]
                        px-4
                        rounded-xl
                        text-[15px]
                        font-medium
                        transition-all
                        duration-200
                        ${
                          isActive(item.path)
                            ? 'text-[#2F6F45] bg-[#EAF5EC]'
                            : 'text-[#718078] hover:text-[#2F6F45] hover:bg-[#F6F8F4]'
                        }
                      `}
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                ))}

                {/* Mobile Actions */}
                <div
                  className="
                    pt-5
                    mt-4
                    border-t
                    border-[#DDE8DF]
                    space-y-3
                  "
                >

                  {/* My Bookings */}
                  <Link
                    to="/my-bookings"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="
                      flex
                      items-center
                      justify-center
                      w-full
                      h-[46px]
                      rounded-xl
                      text-[15px]
                      font-medium
                      text-[#718078]
                      hover:text-[#2F6F45]
                      hover:bg-[#F6F8F4]
                      transition-all
                      duration-200
                    "
                  >
                    My Bookings
                  </Link>

                  {/* Book Now */}
                  <Link
                    to="/book-appointment"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="
                      flex
                      items-center
                      justify-center
                      gap-2
                      w-full
                      h-[48px]
                      rounded-xl
                      bg-[#102018]
                      text-white
                      text-[15px]
                      font-semibold
                      transition-all
                      duration-300
                      hover:bg-[#2F6F45]
                    "
                  >
                    <Phone className="w-[18px] h-[18px]" />
                    <span>Book Now</span>
                  </Link>

                  {/* Quick Book */}
                  <Link
                    to={generateBookingUrl(getTomorrowDate(), 'morning')}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="
                      flex
                      items-center
                      justify-center
                      gap-2
                      w-full
                      h-[48px]
                      rounded-xl
                      bg-[#2F6F45]
                      text-white
                      text-[15px]
                      font-semibold
                      transition-all
                      duration-300
                      hover:bg-[#70BD7D]
                      hover:text-[#102018]
                    "
                  >
                    <Zap className="w-[18px] h-[18px]" />
                    <span>Quick Book</span>
                  </Link>

                </div>

              </div>

            </motion.div>
          )}
        </AnimatePresence>

      </div>

      {/* =====================================================
          RUNNING BAR CSS
      ====================================================== */}
      <style>
        {`
          .announcement-track {
            display: flex;
            width: max-content;
            align-items: center;
            white-space: nowrap;
            animation: announcement-scroll 25s linear infinite;
          }

          .announcement-track span {
            display: inline-block;
            padding-right: 180px;
            color: #ffffff;
            font-size: 15px;
            font-weight: 700;
            font-style: italic;
            letter-spacing: 0.3px;
          }

          @keyframes announcement-scroll {
            0% {
              transform: translateX(0);
            }

            100% {
              transform: translateX(-25%);
            }
          }

          @media (max-width: 640px) {
            .announcement-track span {
              font-size: 13px;
              padding-right: 100px;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .announcement-track {
              animation: none;
            }
          }
        `}
      </style>

    </motion.header>
  );
}

export default Header;